import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileDown, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import PageHero from "@/components/bakery/PageHero";
import { LedgerArt } from "@/components/bakery/Art";
import ProductMultiSelect from "@/components/kartu/ProductMultiSelect";
import { DetailTables, DailyTables } from "@/components/kartu/KartuTables";
import TxnDetailDialog from "@/components/common/TxnDetailDialog";
import { Field, inputCls } from "@/components/common/Field";
import { EmptyState, Loading, OutlineBtn, PrimaryBtn } from "@/components/common/ui";
import { useApi } from "@/lib/useApi";
import { downloadFile, errMsg } from "@/lib/api";
import { CATEGORY_ORDER, shiftDate, todayJkt } from "@/lib/format";

export default function KartuStok() {
  const [sp] = useSearchParams();
  const { data: products } = useApi("/products");
  const [from, setFrom] = useState(shiftDate(todayJkt(), -6));
  const [to, setTo] = useState(todayJkt());
  const [category, setCategory] = useState("");
  const [selected, setSelected] = useState(sp.get("product") ? [sp.get("product")] : []);
  const [daily, setDaily] = useState(false);
  const [onlyMoved, setOnlyMoved] = useState(false);
  const build = (over = {}) => ({ date_from: from, date_to: to, category: category || undefined, product_ids: selected.join(",") || undefined, view: daily ? "daily" : "detail", ts: Date.now(), ...over });
  const [params, setParams] = useState(build);
  const { data, loading, error } = useApi("/kartu-stok", params);
  const [detail, setDetail] = useState(null);

  const refresh = (over) => setParams(build(over));
  const pick = (id) => { setSelected([id]); refresh({ product_ids: id }); };
  const exportAs = (format) => {
    const { ts, ...p } = params;
    downloadFile("/kartu-stok/export", { ...p, format }, `kartu-stok-${p.view}-${p.date_from}_${p.date_to}.${format}`).catch((e) => toast.error(errMsg(e)));
  };
  const shown = data ? { ...data, groups: onlyMoved ? data.groups.filter((g) => g.rows.some((r) => r.txn_no || ["produksi", "purchase", "penjualan", "waste", "adjustment"].some((c) => r[c]))) : data.groups } : null;

  return (
    <div data-testid="kartu-stok-page">
      <PageHero testid="kartu-hero" eyebrow="Ledger · Akhir dari semua alur" title="Kartu Stok" Art={LedgerArt} compact
        desc="Qty Akhir = Qty Awal + Produksi + Purchase − Penjualan − Waste ± Adjustment. Qty Awal hari ini = Qty Akhir kemarin." />
      <div className="mb-6 rounded-xl border-2 border-ink bg-white p-4" data-testid="kartu-filters">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[160px_160px_180px_1fr_auto]">
          <Field label="Dari"><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} data-testid="kartu-from" /></Field>
          <Field label="Sampai"><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} data-testid="kartu-to" /></Field>
          <Field label="Kategori">
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls} data-testid="kartu-category">
              <option value="">Semua kategori</option>{CATEGORY_ORDER.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Produk"><ProductMultiSelect products={products} value={selected} onChange={setSelected} /></Field>
          <div className="flex items-end"><PrimaryBtn className="w-full" onClick={() => refresh()} data-testid="kartu-refresh-btn"><RefreshCw className="h-4 w-4" /> Refresh</PrimaryBtn></div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-zinc-200 pt-4">
          <label className="flex items-center gap-2 text-sm font-medium"><Switch checked={daily} onCheckedChange={(v) => { setDaily(v); refresh({ view: v ? "daily" : "detail" }); }} data-testid="kartu-daily-toggle" /> Ringkasan Harian</label>
          <label className="flex items-center gap-2 text-sm font-medium"><Switch checked={onlyMoved} onCheckedChange={setOnlyMoved} data-testid="kartu-only-moved" /> Hanya produk dengan transaksi</label>
          <div className="flex gap-2 sm:ml-auto">
            <OutlineBtn className="h-10" onClick={() => exportAs("xlsx")} data-testid="kartu-export-xlsx"><FileDown className="h-4 w-4" /> Excel</OutlineBtn>
            <OutlineBtn className="h-10" onClick={() => exportAs("pdf")} data-testid="kartu-export-pdf"><FileDown className="h-4 w-4" /> PDF</OutlineBtn>
          </div>
        </div>
      </div>
      {error && <div className="mb-4 rounded-md bg-brand px-4 py-3 text-sm text-white" data-testid="kartu-error">{error}</div>}
      {loading && !data ? <Loading /> : shown?.groups.length ? (
        <div data-testid="kartu-results">{shown.view === "daily" ? <DailyTables data={shown} onPick={pick} /> : <DetailTables data={shown} onOpen={setDetail} onPick={pick} />}</div>
      ) : <EmptyState Art={LedgerArt} title="Tidak ada data" desc="Ubah filter lalu tekan Refresh." testid="kartu-empty" />}
      <TxnDetailDialog txnId={detail} onClose={() => setDetail(null)} onChanged={() => refresh()} />
    </div>
  );
}
